/**
 * ScheduleFilterDrawer — dynamic filter editor for scheduled reports.
 *
 * The accordion sections change based on the report type:
 *   - AlertDetails config (IDs 1,4,5,10): Areas(3) + Residents(2) + Users(2) + Devices(2) + Events(2+responseTime)
 *   - Wellness config (IDs 2,3,9,11,13): Areas(3) + Residents(2) + Devices(1)
 *   - Environmental config (IDs 6,7,8,12): Areas(2) + Residents(1) + Events(2)
 *
 * No Time Range accordion — the schedule interval is set separately in ScheduleDrawer.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';

// ---------------------------------------------------------------------------
// Section configs per report type group
// ---------------------------------------------------------------------------

const ALERT_SECTIONS = [
    { id: 'areas', label: 'Areas', subs: [
        { key: 'areaTypes', label: 'Area Types', idKey: 'area_type_id', nameKey: 'area_type_name', optionsKey: 'areaTypes' },
        { key: 'areaGroups', label: 'Area Groups', idKey: 'area_group_id', nameKey: 'area_group_name', optionsKey: 'areaGroups' },
        { key: 'areas', label: 'Area', idKey: 'area_id', nameKey: 'area_name', optionsKey: 'areas' },
    ]},
    { id: 'residents', label: 'Residents', subs: [
        { key: 'residents', label: 'Resident', idKey: 'resident_id', nameKey: 'resident_name', optionsKey: 'residents' },
        { key: 'residentGroups', label: 'Resident Groups', idKey: 'resident_group_id', nameKey: 'resident_group_name', optionsKey: 'residentGroups' },
    ]},
    { id: 'users', label: 'Users', subs: [
        { key: 'users', label: 'User', idKey: 'user_id', nameKey: 'display_name', optionsKey: 'users' },
        { key: 'userGroups', label: 'User Groups', idKey: 'group_id', nameKey: 'group_name', optionsKey: 'userGroups' },
    ]},
    { id: 'devices', label: 'Devices', subs: [
        { key: 'deviceCategories', label: 'Device Categories', idKey: 'device_category_id', nameKey: 'device_category_short_name', optionsKey: 'deviceCategories' },
        { key: 'devices', label: 'Devices', idKey: 'device_id', nameKey: 'device_name', optionsKey: 'devices' },
    ]},
    { id: 'events', label: 'Events', subs: [
        { key: 'reasons', label: 'Reasons', idKey: 'reason_code_id', nameKey: 'reason_code_description', optionsKey: 'reasonCodes' },
        // { key: 'eventTypes', label: 'Event Types', idKey: 'event_type_id', nameKey: 'event_type_name', optionsKey: 'eventTypes' },
    ]},
];

const WELLNESS_SECTIONS = [
    { id: 'areas', label: 'Areas', subs: [
        { key: 'areaTypes', label: 'Area Types', idKey: 'area_type_id', nameKey: 'area_type_name', optionsKey: 'areaTypes' },
        { key: 'areaGroups', label: 'Area Groups', idKey: 'area_group_id', nameKey: 'area_group_name', optionsKey: 'areaGroups' },
        { key: 'areas', label: 'Area', idKey: 'area_id', nameKey: 'area_name', optionsKey: 'areas' },
    ]},
    { id: 'residents', label: 'Residents', subs: [
        { key: 'residents', label: 'Resident', idKey: 'resident_id', nameKey: 'resident_name', optionsKey: 'residents' },
        { key: 'residentGroups', label: 'Resident Groups', idKey: 'resident_group_id', nameKey: 'resident_group_name', optionsKey: 'residentGroups' },
    ]},
    { id: 'devices', label: 'Devices', subs: [
        { key: 'deviceCategories', label: 'Device Categories', idKey: 'device_category_id', nameKey: 'device_category_short_name', optionsKey: 'deviceCategories' },
    ]},
];

const ENVIRONMENTAL_SECTIONS = [
    { id: 'areas', label: 'Areas', subs: [
        { key: 'areaTypes', label: 'Area Types', idKey: 'area_type_id', nameKey: 'area_type_name', optionsKey: 'areaTypes' },
        { key: 'areas', label: 'Area', idKey: 'area_id', nameKey: 'area_name', optionsKey: 'areas' },
    ]},
    { id: 'residents', label: 'Residents', subs: [
        { key: 'residents', label: 'Resident', idKey: 'resident_id', nameKey: 'resident_name', optionsKey: 'residents' },
    ]},
    { id: 'events', label: 'Events', subs: [
        { key: 'reasons', label: 'Reasons', idKey: 'reason_code_id', nameKey: 'reason_code_description', optionsKey: 'reasonCodes' },
        // { key: 'eventTypes', label: 'Event Types', idKey: 'event_type_id', nameKey: 'event_type_name', optionsKey: 'eventTypes' },
    ]},
];

/** Map report_type_id → section config */
const ALERT_TYPE_IDS = [1, 4, 5, 10];
const WELLNESS_TYPE_IDS = [2, 3, 9, 11, 13];
const ENVIRONMENTAL_TYPE_IDS = [6, 7, 8, 12];

function getSectionsForType(reportTypeId) {
    const id = parseInt(reportTypeId, 10);
    if (ALERT_TYPE_IDS.includes(id)) return ALERT_SECTIONS;
    if (WELLNESS_TYPE_IDS.includes(id)) return WELLNESS_SECTIONS;
    if (ENVIRONMENTAL_TYPE_IDS.includes(id)) return ENVIRONMENTAL_SECTIONS;
    return ALERT_SECTIONS; // fallback
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function parseFilterTerms(filterTerms) {
    if (!filterTerms) return {};
    try {
        const parsed = typeof filterTerms === 'string' ? JSON.parse(filterTerms) : filterTerms;
        const result = {};

        const keyMap = {
            area_filter: 'areas',
            area_group_filter: 'areaGroups',
            area_type_filter: 'areaTypes',
            resident_filter: 'residents',
            resident_group_filter: 'residentGroups',
            user_filter: 'users',
            user_group_filter: 'userGroups',
            device_category_filter: 'deviceCategories',
            device_filter: 'devices',
            event_type_filter: 'eventTypes',
            reason_filter: 'reasons',
        };

        Object.entries(keyMap).forEach(([dbKey, stateKey]) => {
            const val = parsed[dbKey]?.values;
            if (val && val !== '') {
                result[stateKey] = new Set(String(val).split(',').filter(Boolean));
            } else {
                result[stateKey] = new Set();
            }
        });

        // Response time
        if (parsed.resp_filter_items?.values) {
            const rv = parsed.resp_filter_items.values;
            result.responseTime = {
                enabled: Boolean(rv[0]),
                operator: rv[1] || 'lt',
                value: parseInt(rv[2], 10) || 60,
            };
        } else {
            result.responseTime = { enabled: false, operator: 'lt', value: 60 };
        }

        return result;
    } catch {
        return {};
    }
}

function serializeFilters(working, filterOptions) {
    const result = {};
    const keyMap = {
        areas: { dbKey: 'area_filter', optionsKey: 'areas', idKey: 'area_id', nameKey: 'area_name' },
        areaGroups: { dbKey: 'area_group_filter', optionsKey: 'areaGroups', idKey: 'area_group_id', nameKey: 'area_group_name' },
        areaTypes: { dbKey: 'area_type_filter', optionsKey: 'areaTypes', idKey: 'area_type_id', nameKey: 'area_type_name' },
        residents: { dbKey: 'resident_filter', optionsKey: 'residents', idKey: 'resident_id', nameKey: 'resident_name' },
        residentGroups: { dbKey: 'resident_group_filter', optionsKey: 'residentGroups', idKey: 'resident_group_id', nameKey: 'resident_group_name' },
        users: { dbKey: 'user_filter', optionsKey: 'users', idKey: 'user_id', nameKey: 'display_name' },
        userGroups: { dbKey: 'user_group_filter', optionsKey: 'userGroups', idKey: 'group_id', nameKey: 'group_name' },
        deviceCategories: { dbKey: 'device_category_filter', optionsKey: 'deviceCategories', idKey: 'device_category_id', nameKey: 'device_category_short_name' },
        devices: { dbKey: 'device_filter', optionsKey: 'devices', idKey: 'device_id', nameKey: 'device_name' },
        eventTypes: { dbKey: 'event_type_filter', optionsKey: 'eventTypes', idKey: 'event_type_id', nameKey: 'event_type_name' },
        reasons: { dbKey: 'reason_filter', optionsKey: 'reasonCodes', idKey: 'reason_code_id', nameKey: 'reason_code_description' },
    };

    Object.entries(keyMap).forEach(([stateKey, { dbKey, optionsKey, idKey, nameKey }]) => {
        const set = working[stateKey];
        if (set && set.size > 0) {
            const ids = [...set];
            const options = filterOptions?.[optionsKey] || [];
            const labels = ids.map((id) => {
                const opt = options.find((o) => String(o[idKey]) === id);
                return opt ? opt[nameKey] : id;
            });

            // Reasons CSV combines reason_code_id with the row's matching
            // cloud_reason_code_id so the report runner can match either side.
            let valuesCsv;
            if (stateKey === 'reasons') {
                const cloudIds = ids
                    .map((id) => {
                        const opt = options.find((o) => String(o[idKey]) === id);
                        return opt?.cloud_reason_code_id;
                    })
                    .filter((cid) => cid != null)
                    .map(String);
                const combined = [...new Set([...ids, ...cloudIds])];
                valuesCsv = combined.join(',');
            } else {
                valuesCsv = ids.join(',');
            }

            result[dbKey] = { values: valuesCsv, labels: labels.join(', ') };
        } else {
            result[dbKey] = { values: '', labels: '' };
        }
    });

    // Response time
    if (working.responseTime?.enabled) {
        const rt = working.responseTime;
        const opLabel = rt.operator === 'lt' ? 'less than' : rt.operator === 'gt' ? 'greater than' : 'equal to';
        result.resp_filter_items = {
            values: [true, rt.operator, rt.value],
            labels: `Response Time ${opLabel} ${rt.value} min`,
        };
    } else {
        result.resp_filter_items = { values: [false, 'lt', 60], labels: '' };
    }

    return result;
}

function cloneWorking(w) {
    const result = {};
    Object.keys(w).forEach((k) => {
        if (k === 'responseTime') {
            result[k] = { ...w[k] };
        } else {
            result[k] = new Set(w[k]);
        }
    });
    return result;
}

// ---------------------------------------------------------------------------
// FilterSubSection (reusable)
// ---------------------------------------------------------------------------

const FilterSubSection = ({ subDef, options, selected, onToggle, onSelectAll, onClear }) => {
    const [search, setSearch] = useState('');
    const [showSelectedOnly, setShowSelectedOnly] = useState(false);

    const filteredOptions = useMemo(() => {
        let list = options || [];
        if (showSelectedOnly) list = list.filter((opt) => selected.has(String(opt[subDef.idKey])));
        if (search) {
            const term = search.toLowerCase();
            list = list.filter((opt) => (opt[subDef.nameKey] || '').toLowerCase().includes(term));
        }
        return list;
    }, [options, search, subDef.idKey, subDef.nameKey, selected, showSelectedOnly]);

    return (
        <div className="ar-filter-section">
            <div className="ar-filter-section__header">
                <span className="ar-filter-section__label">{subDef.label}</span>
                <div className="ar-filter-section__actions">
                    <button className="ar-filter-section__btn" onClick={() => onSelectAll(subDef.key, (options || []).map((o) => String(o[subDef.idKey])))} type="button">Select All</button>
                    <button className="ar-filter-section__btn" onClick={() => onClear(subDef.key)} type="button">Clear</button>
                    <button className={`ar-filter-section__btn${showSelectedOnly ? ' ar-filter-section__btn--active' : ''}`} onClick={() => setShowSelectedOnly((p) => !p)} type="button">Show Selected</button>
                </div>
            </div>
            <input className="ar-filter-section__search" type="text" placeholder={`Search ${subDef.label.toLowerCase()}...`} value={search} onChange={(e) => setSearch(e.target.value)} />
            <div className="ar-filter-list">
                {filteredOptions.length === 0 ? (<p className="ar-filter-list__empty">{showSelectedOnly ? 'No selections.' : 'No options found.'}</p>) : null}
                {filteredOptions.map((opt) => {
                    const id = String(opt[subDef.idKey]);
                    return (
                        <label key={id} className="ar-filter-list__item">
                            <input type="checkbox" checked={selected.has(id)} onChange={() => onToggle(subDef.key, id)} />
                            <span>{opt[subDef.nameKey] || '(empty)'}</span>
                        </label>
                    );
                })}
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ScheduleFilterDrawer = ({
    open, onClose, onApply,
    reportTypeId, filterOptions, currentFilterTerms,
}) => {
    const sections = useMemo(() => getSectionsForType(reportTypeId), [reportTypeId]);
    const showResponseTime = ALERT_TYPE_IDS.includes(parseInt(reportTypeId, 10));

    const [working, setWorking] = useState(() => parseFilterTerms(currentFilterTerms));
    const [expandedSections, setExpandedSections] = useState(new Set());

    useEffect(() => {
        if (open) setWorking(parseFilterTerms(currentFilterTerms));
    }, [open, currentFilterTerms]);

    const toggleSection = useCallback((id) => {
        setExpandedSections((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
    }, []);

    const handleToggle = useCallback((key, id) => {
        setWorking((prev) => { const n = cloneWorking(prev); if (!n[key]) n[key] = new Set(); if (n[key].has(id)) n[key].delete(id); else n[key].add(id); return n; });
    }, []);

    const handleSelectAll = useCallback((key, allIds) => {
        setWorking((prev) => { const n = cloneWorking(prev); n[key] = new Set(allIds); return n; });
    }, []);

    const handleClear = useCallback((key) => {
        setWorking((prev) => { const n = cloneWorking(prev); n[key] = new Set(); return n; });
    }, []);

    const handleRespToggle = useCallback((e) => {
        setWorking((prev) => { const n = cloneWorking(prev); n.responseTime = { ...n.responseTime, enabled: e.target.checked }; return n; });
    }, []);

    const handleRespOperator = useCallback((e) => {
        setWorking((prev) => { const n = cloneWorking(prev); n.responseTime = { ...n.responseTime, operator: e.target.value }; return n; });
    }, []);

    const handleRespValue = useCallback((e) => {
        setWorking((prev) => { const n = cloneWorking(prev); n.responseTime = { ...n.responseTime, value: parseInt(e.target.value, 10) || 0 }; return n; });
    }, []);

    const getSectionCount = useCallback((section) => {
        let count = 0;
        section.subs.forEach((sub) => { count += (working[sub.key]?.size || 0); });
        if (section.id === 'events' && showResponseTime && working.responseTime?.enabled) count += 1;
        return count;
    }, [working, showResponseTime]);

    const handleApply = useCallback(() => {
        const serialized = serializeFilters(working, filterOptions);
        onApply(serialized);
    }, [working, filterOptions, onApply]);

    const handleClearAll = useCallback(() => {
        const empty = {};
        sections.forEach((s) => s.subs.forEach((sub) => { empty[sub.key] = new Set(); }));
        empty.responseTime = { enabled: false, operator: 'lt', value: 60 };
        setWorking(empty);
    }, [sections]);

    return (
        <>
            {open ? <div className="dp-drawer-backdrop" onClick={onClose} /> : null}
            <div className={`dp-drawer dp-drawer--xl${open ? ' is-open' : ''}`}>
                <div className="dp-drawer__header">
                    <h2 className="dp-drawer__title">Edit Report Filters</h2>
                    <button className="dp-drawer__close" onClick={onClose}>{'\u2715'}</button>
                </div>

                <div className="dp-drawer__body" style={{ overflowY: 'auto', flex: 1 }}>
                    {sections.map((section) => {
                        const isExpanded = expandedSections.has(section.id);
                        const count = getSectionCount(section);
                        return (
                            <div key={section.id} className="ar-accordion">
                                <button className={`ar-accordion__header${isExpanded ? ' ar-accordion__header--open' : ''}`} onClick={() => toggleSection(section.id)} type="button">
                                    <span className="ar-accordion__icon">{isExpanded ? '\u25BC' : '\u25B6'}</span>
                                    <span className="ar-accordion__label">{section.label}</span>
                                    {count > 0 ? (<span className="ar-accordion__count">{count} selected</span>) : null}
                                </button>
                                {isExpanded ? (
                                    <div className="ar-accordion__body">
                                        {section.id === 'events' && showResponseTime ? (
                                            <div className="ar-filter-section">
                                                <div className="ar-filter-section__header">
                                                    <span className="ar-filter-section__label">Response Time</span>
                                                </div>
                                                <div className="ar-resp-filter">
                                                    <label className="ar-resp-filter__toggle">
                                                        <input type="checkbox" checked={working.responseTime?.enabled || false} onChange={handleRespToggle} />
                                                        <span>Enable</span>
                                                    </label>
                                                    {working.responseTime?.enabled ? (
                                                        <div className="ar-resp-filter__controls">
                                                            <select className="ar-resp-filter__select" value={working.responseTime?.operator || 'lt'} onChange={handleRespOperator}>
                                                                <option value="lt">Less than</option>
                                                                <option value="gt">Greater than</option>
                                                                <option value="eq">Equal to</option>
                                                            </select>
                                                            <input className="ar-resp-filter__input" type="number" value={working.responseTime?.value || 60} onChange={handleRespValue} min={0} />
                                                            <span className="ar-resp-filter__unit">min</span>
                                                        </div>
                                                    ) : null}
                                                </div>
                                            </div>
                                        ) : null}
                                        {section.subs.map((sub) => (
                                            <FilterSubSection key={sub.key} subDef={sub} options={filterOptions?.[sub.optionsKey] || []} selected={working[sub.key] || new Set()} onToggle={handleToggle} onSelectAll={handleSelectAll} onClear={handleClear} />
                                        ))}
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}
                </div>

                <div className="dp-drawer__footer">
                    <button className="dp-btn dp-btn--cancel" onClick={handleClearAll} type="button">Clear All</button>
                    <button className="dp-btn dp-btn--primary" onClick={handleApply} type="button">Apply Filters</button>
                </div>
            </div>
        </>
    );
};

export default ScheduleFilterDrawer;
export { parseFilterTerms, serializeFilters };
